import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import { calendarService } from 'services/calendarService';
import { LoaderContext } from 'components/common/Loader';
import { toSpaceSeparated } from '../../helpers/numberHelper';

const ReservationDetails = ({ t, reservation, commisionValue, currency, visible }) => {
  const { setLoaderVisibility } = useContext(LoaderContext);
  const ownerReservation = reservation?.extendedProps?.ownerReservation;
  const [guestPayment, setGuestPayout] = useState(reservation?.extendedProps?.guestPayment || {});
  const [ownerPayout, setOwnerPayout] = useState(reservation?.extendedProps?.ownerPayout || {});

  const getPayoutDetailsOfReservation = async () => {
    if (reservation?.extendedProps?.confirmationCode && !ownerReservation) {
      try {
        setLoaderVisibility(true);
        const payload = {
          confirmation_codes: [reservation.extendedProps.confirmationCode],
        };
        const payoutDetails = await calendarService.getReservationPayoutDetails(payload);
        setGuestPayout(payoutDetails.guestPayment);
        setOwnerPayout(payoutDetails.ownerPayout);
      } catch (error) {
        console.error(error?.response);
      } finally {
        setLoaderVisibility(false);
      }
    }
  };

  useEffect(() => {
    if (reservation?.extendedProps?.confirmationCode) getPayoutDetailsOfReservation();

    return () => {
      setGuestPayout({});
      setOwnerPayout({});
    };
  }, [reservation?.extendedProps?.confirmationCode]);

  const hideClass = visible ? '' : 'invisible';
  const commision = reservation?.extendedProps
    ? reservation.extendedProps.grossRevenue * commisionValue
    : 1;

  return reservation ? (
    <div className={`reservation-details property-overflow ${hideClass}`}>
      <div className="reservation-header header-grayed flex-row">
        <span>{t('reservation')}</span>
      </div>
      <div className="round-separator separator-gray">
        <div className="circle" />
      </div>

      <div className="rows">
        <div className="reservation-detail-row flex-row">
          <span>{`${t('reservation')}:`}</span>
          <span>{ownerReservation ? 'Owner' : reservation.extendedProps.confirmationCode}</span>
        </div>
        <div className="reservation-detail-row flex-row">
          <span>{`${t('name')}:`}</span>
          <span>{reservation.extendedProps.guestName}</span>
        </div>
        <div className="reservation-detail-row flex-row">
          <span>{`${t('checkIn')}:`}</span>
          <span>{moment(reservation.start).format('D.M.Y')}</span>
        </div>
        <div className="reservation-detail-row flex-row">
          <span>{`${t('checkOut')}:`}</span>
          <span>{moment(reservation.end).format('D.M.Y')}</span>
        </div>
        <div className="reservation-detail-row flex-row">
          <span>{`${t('nights')}:`}</span>
          <span>{reservation.extendedProps.nights}</span>
        </div>
        <div className="reservation-detail-row flex-row">
          <span>{`${t('source')}:`}</span>
          <span>{reservation.extendedProps.source}</span>
        </div>
      </div>

      {/* FINANCES - Only For Owner Reservations */}
      {ownerReservation && (
        <>
          <div className="reservation-header header-grayed flex-row">
            <span>{t('finances')}</span>
          </div>
          <div className="round-separator separator-gray">
            <div className="circle" />
          </div>
          <div className="rows">
            <div className="reservation-detail-row flex-row">
              <span>{`${t('grossRevenue')}:`}</span>
              {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
              <span>{`${toSpaceSeparated(
                reservation.extendedProps.grossRevenue
              )} ${currency}`}</span>
            </div>
            <div className="reservation-detail-row flex-row">
              <span>{`${t('fpManagementFee')}:`}</span>
              <span>{`-${toSpaceSeparated(commision)} ${currency}`}</span>
            </div>
            <div className="reservation-detail-row flex-row">
              <span>{`${t('totalAmount')}:`}</span>
              <span>
                {`${toSpaceSeparated(
                  reservation.extendedProps.grossRevenue - commision
                )} ${currency}`}
              </span>
            </div>
          </div>
        </>
      )}

      {/* GUEST PAYOUT */}
      {!ownerReservation && Object.keys(guestPayment).length > 0 && (
        <>
          <div className="reservation-header header-grayed flex-row">
            <span>{t('guestPayment')}</span>
          </div>
          <div className="round-separator separator-gray">
            <div className="circle" />
          </div>
          <div className="rows">
            <div className="reservation-detail-row flex-row">
              <span>{`${t('accommodationFare')}:`}</span>
              <span>{`${toSpaceSeparated(guestPayment.discounted_fare)} ${currency}`}</span>
            </div>
            <div className="reservation-detail-row flex-row">
              <span>{`${t('cleaningFee')}:`}</span>
              <span>{`${toSpaceSeparated(guestPayment.cleaning_fee)} ${currency}`}</span>
            </div>
            <div className="reservation-detail-row flex-row">
              <span>{`${t('propertyUseTaxes')}:`}</span>
              <span>{`${toSpaceSeparated(guestPayment.property_use_taxes)} ${currency}`}</span>
            </div>
            <div className="reservation-detail-row flex-row">
              <span>{`${t('totalGuestPayment')}:`}</span>
              <span>{`${toSpaceSeparated(guestPayment.total_guest_payment)} ${currency}`}</span>
            </div>
          </div>
        </>
      )}

      {/* OWNER PAYOUT */}
      {!ownerReservation && Object.keys(ownerPayout).length > 0 && (
        <>
          <div className="reservation-header header-grayed flex-row">
            <span>{t('ownerPayout')}</span>
          </div>
          <div className="round-separator separator-gray">
            <div className="circle" />
          </div>
          <div className="rows">
            <div className="reservation-detail-row flex-row">
              <span>{`${t('accommodationFare')}:`}</span>
              <span>{`${toSpaceSeparated(ownerPayout.discounted_fare)} ${currency}`}</span>
            </div>
            <div className="reservation-detail-row flex-row">
              <span>{`${t('bookingPlatformFee')}:`}</span>
              <span>{`-${toSpaceSeparated(ownerPayout.host_service_fee)} ${currency}`}</span>
            </div>
            <div className="reservation-detail-row flex-row">
              <span>{`${t('totalGrossRevenue')}:`}</span>
              <span>{`${toSpaceSeparated(ownerPayout.gross_revenue)} ${currency}`}</span>
            </div>
            <div className="reservation-detail-row flex-row">
              <span>{`${t('fpManagementFee')}:`}</span>
              <span>{`-${toSpaceSeparated(ownerPayout.management_fee)} ${currency}`}</span>
            </div>
            <div className="reservation-detail-row flex-row">
              <span>{`${t('netpayout')}:`}</span>
              <span>{`${toSpaceSeparated(ownerPayout.net_payout)} ${currency}`}</span>
            </div>
          </div>
        </>
      )}
    </div>
  ) : (
    ''
  );
};

export default ReservationDetails;
